/**
 * SetupForm - An organism component for first-time application setup.
 *
 * Combines multiple molecules (Input, PasswordInput, Button) with business logic
 * for creating the first admin user during initial setup.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Input } from '@lean-agility/design-system/react';
import { Button } from '@lean-agility/design-system/react';
import { InlineError } from '@lean-agility/design-system/react';
import { PasswordInput } from '@/features/auth';
import { setupSchema, type SetupFormData } from '../../validation/setupSchemas';
import type { CompleteSetupRequest } from '@/types/api';

export interface SetupFormProps {
  /**
   * Callback invoked when the form is successfully submitted.
   * Receives the validated setup data.
   */
  onSubmit: (data: CompleteSetupRequest) => Promise<void> | void;

  /**
   * Whether the form is in a loading/submitting state.
   * @default false
   */
  isLoading?: boolean;

  /**
   * Error message to display above the form.
   * Typically used for API-level errors.
   */
  error?: string | null;

  /**
   * Additional CSS classes for the form container.
   */
  className?: string;
}

export function SetupForm({ onSubmit, isLoading = false, error, className }: SetupFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SetupFormData>({
    resolver: zodResolver(setupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const handleFormSubmit = async (data: SetupFormData) => {
    await onSubmit({
      name: data.name,
      email: data.email,
      password: data.password,
    });
  };

  const isFormLoading = isLoading || isSubmitting;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={className} noValidate>
      <div className="space-y-4">
        {/* Global error message */}
        {error && <InlineError error={error} />}

        {/* Name input */}
        <Input
          {...register('name')}
          type="text"
          label="Admin Name"
          placeholder="Your name"
          autoComplete="name"
          error={errors.name?.message}
          disabled={isFormLoading}
          autoFocus
        />

        {/* Email input */}
        <Input
          {...register('email')}
          type="email"
          label="Admin Email"
          placeholder="admin@example.com"
          autoComplete="email"
          error={errors.email?.message}
          disabled={isFormLoading}
        />

        {/* Password input */}
        <PasswordInput
          {...register('password')}
          label="Password"
          placeholder="Enter a secure password"
          autoComplete="new-password"
          error={errors.password?.message}
          disabled={isFormLoading}
        />

        {/* Confirm password input */}
        <PasswordInput
          {...register('confirmPassword')}
          label="Confirm Password"
          placeholder="Confirm your password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          disabled={isFormLoading}
        />

        {/* Submit button */}
        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isFormLoading}>
          {isFormLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Setting up...
            </>
          ) : (
            'Complete Setup'
          )}
        </Button>
      </div>
    </form>
  );
}
